import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/i18n';
import { api } from './client';
import { ApiError } from './types';
import { cityOf } from '@/types/airport';
import type { AirportCity } from '@/types/airport';

/**
 * The station list.
 *
 * Lives in `lib/` rather than in a feature because more than one screen needs it and
 * a feature may not import from another feature.
 *
 * Kazakh airports only: this is the list of stations the operation runs, not every
 * airport in the world. `/airport/all` returns about 9 400 rows, which is a fine
 * answer to a different question and a terrible dropdown.
 */

const KZ = 'KZ';

/**
 * Fetched once per page load and kept.
 *
 * The dictionary changes when someone adds an airport — not while a shift is being
 * worked — so re-fetching it per screen would be a request per navigation for data
 * that never moved. A reload picks up a change, which is soon enough.
 */
let cache: Promise<AirportCity[]> | null = null;

export const fetchStations = (): Promise<AirportCity[]> => {
  cache ??= api
    .get<AirportCity[]>(`/airport/with-city/country/${KZ}`)
    .then((response) => (Array.isArray(response) ? response : []))
    .catch((caught: unknown) => {
      // Clear the cache so the next screen can try again: a failed dictionary must
      // not be remembered as an empty one for the rest of the session.
      cache = null;
      throw caught;
    });

  return cache;
};

/**
 * Forgets the cached station list.
 *
 * The cache above assumes the dictionary does not move while a shift is worked,
 * which is true right up until somebody edits it — and the admin screen that does
 * that is in the same tab. Without this, adding a Kazakh airport leaves it missing
 * from the board's station dropdown until the page is reloaded, which reads as the
 * write not having worked.
 */
export const invalidateStations = (): void => {
  cache = null;
};

interface StationsState {
  stations: AirportCity[];
  isLoading: boolean;
  /** True when the list could not be loaded — the dropdown then offers only "all". */
  hasFailed: boolean;
}

/**
 * The stations, for a filter dropdown.
 *
 * A failure here is not fatal: the table still works, the station filter just cannot
 * be picked from a list. So it reports the failure rather than throwing, and the
 * caller decides how quiet to be about it.
 */
export const useStations = (): StationsState => {
  const [state, setState] = useState<StationsState>(() => ({
    stations: [],
    isLoading: true,
    hasFailed: false,
  }));

  useEffect(() => {
    let active = true;

    fetchStations()
      .then((stations) => {
        if (active) setState({ stations, isLoading: false, hasFailed: false });
      })
      .catch(() => {
        if (active) setState({ stations: [], isLoading: false, hasFailed: true });
      });

    return () => {
      active = false;
    };
  }, []);

  return state;
};

/**
 * Airports the board has had to look up by code, kept for the session.
 *
 * The station list above answers for Kazakh airports and nothing else, but a board
 * shows both ends of every flight and the far end is routinely abroad — so labelling
 * the table from stations alone would print a city beside half the codes and leave the
 * other half bare, which reads as the lookup being broken rather than as the airport
 * being foreign.
 *
 * A code is fetched at most once per session. `null` means "asked, and the dictionary
 * has no such airport" — a real answer worth remembering, so the same missing code is
 * not asked about again on every page. A REQUEST that fails records nothing, because a
 * dropped connection is not an answer and the next page should try again.
 */
const resolved = new Map<string, AirportCity | null>();
const pending = new Map<string, Promise<void>>();

const resolveAirport = (code: string): Promise<void> => {
  const already = pending.get(code);
  if (already) return already;

  const request = searchAirports(code)
    .then((results) => {
      // The search matches city names too, so `OSS` can come back with more than the
      // airport called OSS. Only an exact code is this code.
      resolved.set(
        code,
        results.find((airport) => (airport.airportIataCode ?? '').toUpperCase() === code) ?? null,
      );
    })
    .catch(() => {
      // Deliberately not recorded — see above.
    })
    .finally(() => {
      pending.delete(code);
    });

  pending.set(code, request);
  return request;
};

/**
 * City names for a set of airport codes: `code => "Almaty"`, or null when nothing is
 * known about it.
 *
 * Give it the codes actually on screen. Stations are free — they are already in hand —
 * and anything else costs one request per code, once, for as long as the tab is open.
 *
 * The returned lookup is a function rather than a map so a caller can pass a nullable
 * code straight from a row without checking it first.
 */
export const useAirportCities = (
  codes: readonly (string | null)[],
): ((code: string | null) => AirportCity | null) => {
  const { stations } = useStations();

  // Sorted and joined into a string so the effect below depends on WHICH codes are
  // wanted, not on the identity of the array the caller happened to build this render.
  const wanted = useMemo(() => {
    const unique = new Set(
      codes.flatMap((code) => {
        const upper = (code ?? '').trim().toUpperCase();
        return upper === '' ? [] : [upper];
      }),
    );
    return [...unique].sort();
  }, [codes]);

  const byCode = useMemo(() => {
    const map = new Map<string, AirportCity>();
    for (const station of stations) {
      const code = (station.airportIataCode ?? '').toUpperCase();
      if (code !== '') map.set(code, station);
    }
    return map;
  }, [stations]);

  // Bumped when a lookup lands, so the rows that were showing a bare code re-render
  // with the city. The records live in the module cache above rather than in state:
  // state here is the signal that it changed, not the copy of it.
  const [, setFetched] = useState(0);

  useEffect(() => {
    let active = true;

    const missing = wanted.filter((code) => !byCode.has(code) && !resolved.has(code));
    if (missing.length === 0) return undefined;

    void Promise.all(missing.map(resolveAirport)).then(() => {
      if (active) setFetched((count) => count + 1);
    });

    return () => {
      active = false;
    };
  }, [wanted, byCode]);

  return (code) => {
    const upper = (code ?? '').trim().toUpperCase();
    if (upper === '') return null;
    return byCode.get(upper) ?? resolved.get(upper) ?? null;
  };
};

const NO_CODES: readonly string[] = [];

/**
 * A station dropdown's options: `Almaty — ALA`, ordered by city.
 *
 * `only` narrows it to a reader's own airports — see `useAccessStations` in
 * app/flightAccess.tsx — and `null` offers every station. Those airports are named
 * through `useAirportCities` rather than the station list alone, because an access may
 * name an airport abroad, and a bare code there would read as the lookup having failed.
 *
 * Shared by the board and the international arrivals journal, which had each built the
 * same list.
 */
export const useStationOptions = (
  only: readonly string[] | null,
): { value: string; label: string }[] => {
  const { locale } = useTranslation();
  const { stations } = useStations();
  const airportOf = useAirportCities(only ?? NO_CODES);

  const entries =
    only === null
      ? stations.flatMap((station) => {
          const code = (station.airportIataCode ?? '').trim().toUpperCase();
          return code === '' ? [] : [{ code, city: cityOf(station, locale) }];
        })
      : only.map((code) => {
          const airport = airportOf(code);
          return { code, city: airport ? cityOf(airport, locale) : '' };
        });

  return entries
    .sort((left, right) => (left.city || left.code).localeCompare(right.city || right.code, locale))
    .map(({ code, city }) => ({ value: code, label: city ? `${city} — ${code}` : code }));
};

/**
 * Airports matching a typed query, asked of the server.
 *
 * The station list above is the operation's own airports and answers "which board".
 * This answers "which airport in the world", which is a different question and far too
 * big to send to a browser: `/airport/all` is about 9 400 rows. So the search stays on
 * the server, and the caller passes a signal because the answer to `AL` is worthless
 * once `ALM` has been typed.
 */
export const searchAirports = (query: string, signal?: AbortSignal): Promise<AirportCity[]> =>
  api
    // Encoded, because the query is a path segment and cities have spaces in them:
    // `/airport/search/new%20y`.
    .get<AirportCity[]>(`/airport/search/${encodeURIComponent(query)}`, undefined, signal)
    .then((response) => (Array.isArray(response) ? response : []));

/** Long enough to finish a word, short enough that the list keeps up with typing. */
const SEARCH_DEBOUNCE_MS = 300;

export interface AirportSearchState {
  results: AirportCity[];
  isSearching: boolean;
  hasFailed: boolean;
}

/**
 * Airports matching a query, from the server.
 *
 * Here rather than beside the board's airport filter because the user screen picks
 * airports the same way, and a feature may not import from another feature.
 *
 * Debounced, and every request carries an abort signal: `ALM` typed in three
 * keystrokes must not leave three answers racing, and the two earlier ones are wrong
 * by the time they arrive. An empty query asks nothing.
 *
 * Loading is DERIVED, not a flag set in an effect: the state holds the last outcome
 * together with the query it answered, and searching means "the answer in hand is not
 * for the question on screen". A `setIsSearching(true)` in the effect would be the
 * same shape of bug as the state mirror the table's URL handling exists to avoid.
 */
export const useAirportSearch = (query: string): AirportSearchState => {
  const [outcome, setOutcome] = useState<{
    query: string;
    results: AirportCity[];
    failed: boolean;
  } | null>(null);

  useEffect(() => {
    if (query === '') return;

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      searchAirports(query, controller.signal)
        .then((results) => setOutcome({ query, results, failed: false }))
        .catch((caught: unknown) => {
          // An abort is this hook doing its job, not a failure to report.
          if (caught instanceof ApiError && caught.status === 0 && controller.signal.aborted) return;
          if (caught instanceof DOMException && caught.name === 'AbortError') return;
          setOutcome({ query, results: [], failed: true });
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  if (query === '') return { results: [], isSearching: false, hasFailed: false };

  return {
    // The previous answer is kept on screen while the next one is fetched: a list that
    // empties on every keystroke flickers, and the footnote already says it is stale.
    results: outcome?.failed ? [] : outcome?.results ?? [],
    isSearching: outcome?.query !== query,
    hasFailed: outcome?.query === query && outcome.failed,
  };
};
